import { renderElementToPDF } from './elementRenderer.jsx';
import { preloadImages, getPreloadedImage } from './imagePreloader.js';
import '../../assets/fonts/Andika-Regular-normal.js';
import '../../assets/fonts/GreatVibes-Regular-normal.js';

/**
 * Constants for PDF rendering and compliance with print services
 */
const PDF_RENDERING = {
  // Make sure elements are properly categorized for layering
  ELEMENT_TYPES: {
    IMAGE: ['image'],
    SHAPE: ['shape', 'rectangle', 'circle'],
    TEXT: ['text']
  },
  // PDF syntax compliance flags
  COMPLIANCE: {
    FLATTEN_TRANSPARENCY: true,
    EMBED_FONTS: true,
    PDF_VERSION: 1.4  // PDF/X-1a compatible
  }
};

/**
 * Helper to ensure the current font is properly embedded in the PDF
 * This is critical for Lulu PDF compliance which requires all fonts to be embedded
 * @param {Object} doc - jsPDF instance
 * @param {string} fontName - Name of the font to ensure is embedded
 */
export function ensureFontEmbedding(doc, fontName = 'Baskervville-Regular') {
  if (!PDF_RENDERING.COMPLIANCE.EMBED_FONTS) return;
  
  // Get current font details
  const currentFont = doc.getFont();
  console.log('Current font before text rendering:', currentFont.fontName);
  
  // If using a non-standard font, make sure it's properly set up
  if (currentFont.fontName === fontName || 
      !currentFont.isStandardFont) {
    return; // Already using our custom font with proper embedding
  }
  
  // Otherwise, ensure we're using our properly embedded font
  console.log(`Switching to embedded ${fontName} font for text rendering`);
  doc.setFont(fontName, 'normal');
  
  // Verify font was properly embedded
  const verifyFont = doc.getFont();
  if (verifyFont.fontName !== fontName) {
    console.warn(`⚠️ Font embedding failed for ${fontName}. Using fallback font.`);
    // Use a standard font as fallback to prevent PDF errors
    doc.setFont('helvetica', 'normal');
  }
}

/**
 * Renders a text element to the PDF
 * @param {Object} doc - jsPDF instance
 * @param {Object} element - Text element to render
 */
export function renderTextToPDF(doc, element) {
  // Ensure proper font embedding before rendering text
  ensureFontEmbedding(doc);
}

/**
 * Renders a page with its elements to the PDF
 * @param {Object} doc - jsPDF document
 * @param {Array} elements - Elements to render
 * @param {Object} options - Rendering options
 */
export async function renderPageToPDF(doc, elements, options = {}) {
  const { scale = 1, isCover = false, coverColor, debug = false, pageNumber, hasPageNumbers = false, width, height } = options;

  try {
    // Log elements for debugging
    if (elements && elements.length > 0) {
      console.log(`🎨 Rendering ${elements.length} elements to PDF`);
    } else {
      console.warn(`⚠️ No elements to render for ${isCover ? 'cover' : 'page ' + pageNumber}`);
    }
    
    // If this is a cover page and has a color, fill the background
    if (isCover && coverColor) {
      // Save graphics state
      doc.saveGraphicsState();

      // Set fill color and draw rectangle
      doc.setFillColor(coverColor);
      doc.rect(
        0,
        0,
        doc.internal.pageSize.getWidth(),
        doc.internal.pageSize.getHeight(),
        'F'
      );

      // Restore graphics state
      doc.restoreGraphicsState();
    }

    // For debugging - add visual indicator for empty pages
    if (debug && (!elements || elements.length === 0)) {
      // Draw a border around the page to indicate it's being processed
      doc.setLineWidth(2);
      doc.setDrawColor(255, 0, 0); // Red border for debugging
      doc.rect(
        5,
        5,
        doc.internal.pageSize.getWidth() - 10,
        doc.internal.pageSize.getHeight() - 10,
        'S'
      );
      
      doc.setFontSize(16);
      doc.setTextColor(255, 0, 0);
      doc.text(
        `Empty page ${pageNumber || ''} (no elements)`, 
        doc.internal.pageSize.getWidth() / 2, 
        doc.internal.pageSize.getHeight() / 2, 
        { align: 'center' }
      );
    }

    // Sort elements - IMPORTANT: Sort ONLY by z-index to respect user's layering
    if (!elements || elements.length === 0) {
      return; // No elements to render
    }
    
    // Create border shape elements for images with borders (rendered ON TOP of images)
    const borderShapes = [];
    elements.forEach(element => {
      if (element.type === 'image' && element.borderRadius && element.hasBorder) {
        const borderShape = {
          id: `${element.id}-border`,
          type: 'shape',
          shapeType: 'roundedRect',
          position: { ...element.position },
          size: { ...element.size },
          style: {
            fill: element.borderColor || '#ffffff',
            stroke: element.borderColor || '#ffffff',
            strokeWidth: element.borderWidth || 8,
            borderRadius: element.borderRadius,
            zIndex: (element.style?.zIndex || element.zIndex || 0) + 0.5
          },
          borderRadius: element.borderRadius,
          borderStyle: element.borderStyle || 'solid',
          borderWidth: element.borderWidth || 8,
          borderColor: element.borderColor || '#ffffff',
          borderOpacity: element.borderOpacity !== undefined ? element.borderOpacity : 100,
          _isBorderShape: true
        };
        borderShapes.push(borderShape);
        console.log('[PDF Border] Created border shape for image:', element.id, 'borderRadius:', element.borderRadius, 'borderStyle:', element.borderStyle);
      }
    });
    
    // Combine all elements (originals + auto-generated borders)
    const allElements = [...elements, ...borderShapes];
    
    // Sort ALL elements by z-index only - respect user's layering arrangement
    const sortedElements = allElements.sort((a, b) => {
      const aZIndex = a.style?.zIndex || a.zIndex || 0;
      const bZIndex = b.style?.zIndex || b.zIndex || 0;
      return aZIndex - bZIndex; // Lower z-index rendered first (bottom layer)
    });
    
    // Get image elements for preloading
    const images = sortedElements.filter(e => e.type === 'image');

    // Preload images for this page to avoid per-image stalls during render
    // Skip if forcing dataURL images (we'll fetch per-image as data URL instead)
    let preloaded = null;
    if (!options.omitImages && images.length > 0 && !options.forceDataURLImages) {
      try {
        preloaded = await preloadImages(images, {
          flattenTransparency: false,
          timeout: 8000,
          concurrency: 2,
          overallTimeout: 9000
        });
      } catch (preErr) {
        console.warn('Image preloading skipped due to error:', preErr);
      }
    }

    // Ensure Baskervville-Regular font is set before rendering
    doc.setFont('Baskervville-Regular', 'normal');

    // Render elements
    let omitImagesNow = !!options.omitImages; // per-page kill-switch after first image error
    for (const element of sortedElements) {
      try {
        // Log each element being rendered
        console.log(`📝 Rendering ${element.type} element:`, {
          id: element.id,
          position: element.position,
          type: element.type
        });
        
        // If available, attach preloaded image reference so renderer can skip network load
        if (preloaded && element.type === 'image' && element.src) {
          const pre = getPreloadedImage(preloaded, element.src);
          if (pre) element._preloadedImage = pre;
        }

        await renderElementToPDF(doc, element, { 
          scale, 
          yScale: scale, 
          debug, 
          omitImages: omitImagesNow,
          forceDataURLImages: options.forceDataURLImages,
          fastSkipBrokenImages: true,
          silentSkipBrokenImages: true
        });
        
        // Reset font after each element to ensure consistency
        doc.setFont('Baskervville-Regular', 'normal');
      } catch (elementError) {
        console.error(`⚠️ Error rendering ${element.type} element:`, elementError);
        // If the failing element is an image, disable images for the rest of this page
        if (element && element.type === 'image') {
          omitImagesNow = true;
        }
        // Continue with other elements
      }
    }
    
    // Add page numbers if enabled (not for cover pages)
    if (hasPageNumbers && pageNumber && !isCover) {
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100); // Gray color for page numbers
      doc.setFont('Baskervville-Regular', 'normal');
      
      // Position page number at bottom center of page
      const pageWidth = width || doc.internal.pageSize.getWidth();
      const pageHeight = height || doc.internal.pageSize.getHeight();
      
      doc.text(
        pageNumber.toString(), 
        pageWidth / 2, 
        pageHeight - 28,
        { align: 'center' }
      );
    }
    
    // Add page number for debugging if requested
    if (debug && pageNumber) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 255); // Blue for page numbers
      doc.text(
        `Page ${pageNumber}`, 
        doc.internal.pageSize.getWidth() - 20, 
        doc.internal.pageSize.getHeight() - 10
      );
    }
  } catch (error) {
    console.error('❌ Error rendering page:', error);
    // Continue with other pages even if one fails
  }
}
